import { ProductData, ProductShortData } from './data/product';
import { ProductCardData } from './data/listing';
import { ListingData } from './data/listing';
import { OrderData } from './data/order';
import { commonDynamic, SubscriptionData } from './data/commonDynamic';
import { RecipeData } from './data/recipe';
import { CartData } from './data/cart';

import { jsonfetch } from './functions/fetch';

export namespace DataLayer {

    var resolvepageview: ()=>void;
    var pageviewed = new Promise<void>(resolve => {
        resolvepageview = () => {
            resolve();
        }
    });

    function facebookConversionAPI(dataLayer: any) {
        jsonfetch('https://kuchniasklep.pl/api/fbconversion.php', dataLayer);
    }

    function customerData() {
        const customer = commonDynamic.get('customer');
        const customerDataAvaliable = commonDynamic.get('loggedIn') && customer;

        return {
            customerDataAvailable: customerDataAvaliable,
            customerEmail: customerDataAvaliable ? customer.email : undefined,
            customerFirstName: customerDataAvaliable ? customer.firstName : undefined,
            customerLastName: customerDataAvaliable ? customer.lastName : undefined,
            customerSubscriberStatus: customerDataAvaliable ? (customer.subscriber ? 1 : undefined) : undefined,
            customerCountryISO2: customerDataAvaliable ? customer.countryISO2 : undefined,
            customerCity: customerDataAvaliable ? customer.city : undefined,
            customerPhone: customerDataAvaliable ? customer.phone : undefined,
            customerCurrency: customerDataAvaliable ? customer.currency : undefined
        }
    }

    export async function pageview(eventID: string) {
        // @ts-ignore: Crypto type error
        eventID = crypto.randomUUID();

        const data = {
            event: 'ks.pageview',
            facebookEventId: eventID,
            ...customerData()
        };

        window.dataLayer?.push(data);
        resolvepageview();
        facebookConversionAPI(data);
    }

    export async function product(product: ProductData, eventID: string = "") {
        await pageviewed;

        // @ts-ignore: Crypto type error
        eventID = crypto.randomUUID();

        const categories = product.breadcrumbs;
        const data = {
            event: 'ks.product',
            facebookEventId: eventID,
            ...customerData(),

            productId: product.id,
            productName: product.name,
            productPrice: product.currentPrice,
            productCurrency: product.currency,
            productImage: product?.images.length > 0 ? relativeToAbsolute(product.images[0].full.url) : undefined,
            productURL: relativeToAbsolute(document.location.href),
            productSKU: product.model,
            productBrand: product.brand.name,
            productCategory: categories[categories.length-1].name,
            productAvailability: product.shippingTime,
            productQuantity: 1,
            productCategories: product.categories,

            ecomm_prodid: product.id,
            ecomm_pagetype: 'product',
            ecomm_totalvalue: product.currentPrice,

            ecommerce: {
                items: enchancedEcommerceItems([product])
            },

            uaecommerce: { ecommerce: {
                currencyCode: product.currency,
                impressions: UAenchancedEcommerceItems([product])
            }}
        };

        window.dataLayer?.push({ ecommerce: null });
        window.dataLayer?.push(data);
        facebookConversionAPI(data);
    }

    export async function listing(listing: ListingData) {
        await pageviewed;

        const type = listing.type == "category" ? "category" :
                     listing.type == "manufacturer" ? "manufacturer" :
                     listing.type == "search" ? "search" :
                     null;

        if(!type) return;

        window.dataLayer?.push({ ecommerce: null });
        window.dataLayer?.push({
            event: `ks.listing`,
            ...customerData(),

            type: type,
            listingCategories: type == "category" ? listing.breadcrumbs.filter(category => category.id != "0") : undefined,
            listingProducts: listing.products,
            ecommerce: {
                items: enchancedEcommerceItems(listing.products)
            },
            uaecommerce: { ecommerce: {
                currencyCode: listing.products?.[0]?.currency,
                impressions: UAenchancedEcommerceItems(listing.products)
            }}
        });
    }

    export async function addToCart(product: ProductShortData, eventID: string) {
        await pageviewed;

        // @ts-ignore: Crypto type error
        eventID = crypto.randomUUID();

        const data = {
            event: 'ks.addToCart',
            facebookEventId: eventID,
            ...customerData(),

            productId: product.id,
            productName: product.name,
            productPrice: product.currentPrice,
            productCurrency: product.currency,
            productQuantity: product.quantity,
            productImage: relativeToAbsolute(product.imageFull),
            productURL: relativeToAbsolute(product.link),
            productSKU: product.sku,
            productCategories: product.categories,

            ecommerce: {
                items: enchancedEcommerceItems([product])
            },

            uaecommerce: { ecommerce: {
                currencyCode: product.currency,
                add: {
                    products: UAenchancedEcommerceItems([product])
                }
            }}
        };

        window.dataLayer?.push({ ecommerce: null });
        window.dataLayer?.push(data);
        facebookConversionAPI(data);
    }

    export async function removeFromCart(product: ProductShortData) {
        await pageviewed;

        window.dataLayer?.push({ ecommerce: null });
        window.dataLayer?.push({
            event: 'ks.removeFromCart',
            ...customerData(),

            productId: product.id,
            productName: product.name,
            productPrice: product.currentPrice,
            productCurrency: product.currency,
            productQuantity: product.quantity,
            productImage: relativeToAbsolute(product.imageFull),
            productURL: relativeToAbsolute(product.link),
            productSKU: product.sku,
            productCategories: product.categories,

            ecommerce: {
                items: enchancedEcommerceItems([product])
            },

            uaecommerce: { ecommerce: {
                currencyCode: product.currency,
                remove: {
                    products: UAenchancedEcommerceItems([product])
                }
            }}
        });
    }

    export async function addToFavourites(product: ProductShortData) {
        await pageviewed;

        window.dataLayer?.push({ ecommerce: null });
        window.dataLayer?.push({
            event: 'ks.addToFavourites',
            ...customerData(),

            productId: product.id,
            productName: product.name,
            productPrice: product.currentPrice,
            productCurrency: product.currency,
            productQuantity: product.quantity,
            productImage: relativeToAbsolute(product.imageFull),
            productURL: relativeToAbsolute(product.link),
            productSKU: product.sku,
            productCategories: product.categories,

            ecommerce: {
                items: enchancedEcommerceItems([product]),
                value: product.currentPrice,
                currency: product.currency
            }
        });
    }

    export async function view_cart(cart: CartData) {
        await pageviewed;

        window.dataLayer?.push({ ecommerce: null });
        window.dataLayer?.push({
            event: 'ks.viewCart',
            ...customerData(),

            cartCurrency: cart.currency,
            cartProductValue: cart.productValue,
            cartValue: cart.totalValue,
            cartProducts: cart.products,

            ecomm_prodid: JSON.stringify(Object.values(cart.products).map(product => product.id)),
            ecomm_pagetype: 'cart',
            ecomm_totalvalue: cart.productValue,

            ecommerce: {
                items: enchancedEcommerceItems(Object.values(cart.products)),
                value: cart.productValue,
                currency: cart.currency
            },

            uaecommerce: { ecommerce: {
                checkout: {
                    actionField: {step: 1},
                    currencyCode: cart.currency,
                    products: UAenchancedEcommerceItems(Object.values(cart.products))
                }
            }}
        });
    }

    export async function order_checkout(order: OrderData, eventID: string) {
        await pageviewed;

        // @ts-ignore: Crypto type error
        eventID = crypto.randomUUID();

        const data = {
            event: 'ks.checkout',
            facebookEventId: eventID,
            ...customerData(),

            orderProducts: order.products,
            orderValue: order.totalValue,
            orderProductValue: order.productValue,
            orderCurrency: order.currency,
            orderShipping: order.shippingValue,
            orderShippingTime: order.totalShippingTimeDays,
            orderCoupon: order.coupon,

            ecommerce: {
                items: enchancedEcommerceItems(order.products)
            },

            uaecommerce: { ecommerce: {
                checkout: {
                    actionField: {step: 3},
                    currencyCode: order.currency,
                    products: UAenchancedEcommerceItems(order.products)
                }
            }}
        };

        window.dataLayer?.push({ ecommerce: null });
        window.dataLayer?.push(data);
        facebookConversionAPI(data);
    }

    export async function order_form(order: OrderData, eventID: string) {
        await pageviewed;

        // @ts-ignore: Crypto type error
        eventID = crypto.randomUUID();

        const data = {
            event: 'ks.orderForm',
            facebookEventId: eventID,
            ...customerData(),

            orderProducts: order.products,
            orderValue: order.totalValue,
            orderProductValue: order.productValue,
            orderCurrency: order.currency,
            orderShipping: order.shippingValue,
            orderShippingTime: order.totalShippingTimeDays,
            orderCoupon: order.coupon,

            ecommerce: {
                items: enchancedEcommerceItems(order.products)
            },

            uaecommerce: { ecommerce: {
                checkout: {
                    actionField: {step: 2},
                    currencyCode: order.currency,
                    products: UAenchancedEcommerceItems(order.products)
                }
            }}
        };

        window.dataLayer?.push({ ecommerce: null });
        window.dataLayer?.push(data);
        facebookConversionAPI(data);
    }
    
    export async function order_placed(order: OrderData, eventID: string) {
        await pageviewed;

        // @ts-ignore: Crypto type error
        eventID = crypto.randomUUID();

        const data = {
            event: 'ks.order',
            facebookEventId: eventID,
            ...customerData(),

            orderProducts: order.products,
            orderId: order.id,
            orderValue: order.totalValue,
            orderProductValue: order.productValue,
            orderCurrency: order.currency,
            orderShipping: order.shippingValue,
            orderShippingTime: order.totalShippingTimeDays,
            orderCoupon: order.coupon,

            ecomm_prodid: JSON.stringify(Object.values(order.products).map(product => product.id)),
            ecomm_pagetype: 'purchase',
            ecomm_totalvalue: order.productValue,

            ecommerce: {
                transaction_id: order.id,
                value: order.totalValue,
                currency: order.currency,
                shipping: order.shippingValue,
                coupon: order.coupon,
                items: enchancedEcommerceItems(order.products)
            },

            uaecommerce: { ecommerce: {
                purchase: {
                    currencyCode: order.currency,
                    actionField: {
                        id: order.id,
                        revenue: order.totalValue,
                        shipping: order.shippingValue,
                        coupon: order.coupon
                    },
                    products: UAenchancedEcommerceItems(order.products)
                }
            }}
        };

        window.dataLayer?.push({ ecommerce: null });
        window.dataLayer?.push(data);
        facebookConversionAPI(data);
    }

    export async function search(query: string) {
        await pageviewed;

        window.dataLayer?.push({
            event: 'ks.search',
            searchQuery: query,
            ...customerData(),
        });
    }

    export async function subscribe(subscription: SubscriptionData) {
        await pageviewed;

        window.dataLayer?.push({
            event: 'ks.subscribe',
            ...customerData(),

            subscriptionPlace: subscription.place,
            subscriptionEmail: subscription.email,
            subscriptionName: subscription?.name,
            subscriptionStatus: subscription.subscriber ? 1 : 0
        });
    }

    export async function recipe(recipe: RecipeData) {
        await pageviewed;

        window.dataLayer?.push({
            event: 'ks.recipe',
            ...customerData(),

            recipeName: recipe.title,
            recipeCategory: recipe.category,
            recipeCuisine: recipe.cuisine
        });
    }

    function relativeToAbsolute(url: string) {
        return url.includes('http') ? url : document.baseURI + url;
    }

    function enchancedEcommerceItems(products: ProductData[] | ProductShortData[] | ProductCardData[]) {
        return products.map( (product, index) => { 
            const categories = (product as ProductShortData).breadcrumbs.filter(category => category.name != "Strona główna").map(category => category.name).reverse();

            return {
                item_id: product.id,
                item_name: product.name,
                currency: product.currency,
                index: index,
                price: product.currentPrice,
                item_brand: product.brandName,
                item_category: categories?.[0],
                item_category2: categories?.[1],
                item_category3: categories?.[2],
                item_category4: categories?.[3],
                item_category5: categories?.[4],
            }
        })
    }

    function UAenchancedEcommerceItems(products: ProductData[] | ProductShortData[] | ProductCardData[]) {
        return products.map( (product, index) => { 


            const categories = (product as ProductShortData).breadcrumbs.filter(category => category.name != "Strona główna").map(category => category.name);

            return {
                name: product.name,
                id: product.id,
                price: product.currentPrice,
                brand: product.brandName,
                category: categories.join('/'),
                position: index+1,
                quantity: product.quantity
            };
        })
    }
}